import axios from 'axios';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Define the types for the pod metrics response
export interface PodMetric {
  cluster: string;
  namespace: string;
  pod_name: string;
  container_name: string;
  node_name: string;
  phase: string;
  description: string;
  display_name: string;
  restart_count: number;
  cpu_usage: {
    used: string;
    requested: string;
    limited: string;
    percent: number;
  };
  memory_usage: {
    used: string;
    requested: string;
    limited: string;
    percent: number;
  };
  created_at: string;
  restarted_at?: string;
  timestamp: string;
  event_reason?: string;
  record_type?: string;
  log_capture?: {
    log_lines: string[];
    capture_reason: string;
    capture_time: string;
    previous_container: boolean;
    log_level: string;
    max_lines: number;
  };
}

export interface PodMetricsResponse {
  metrics: PodMetric[];
  count: number;
  start_time: string;
  end_time: string;
  cluster: string;
}

// Define the state interface
interface PodMetricsState {
  metrics: PodMetric[];
  historicalMetrics: PodMetric[];
  count: number;
  cluster: string;
  loading: boolean;
  loadingHistory: boolean;
  error: string | null;
  historyError: string | null;
}

const initialState: PodMetricsState = {
  metrics: [],
  historicalMetrics: [],
  count: 0,
  cluster: '',
  loading: false,
  loadingHistory: false,
  error: null,
  historyError: null,
};

// Set the API base URL - use the proxy to avoid CORS issues
const POD_METRICS_API_URL = 'https://api.status.gridbit.live/api/v1/pods/metrics';
const POD_METRICS_HISTORY_API_URL = 'https://api.status.gridbit.live/api/v1/pods/metrics/history';

// Create async thunk for fetching pod metrics
export const fetchPodMetrics = createAsyncThunk(
  'podMetrics/fetchPodMetrics',
  async (cluster: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${POD_METRICS_API_URL}?cluster=${cluster}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching pod metrics:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch pod metrics');
    }
  }
);

// Interface for historical metrics request
export interface HistoricalMetricsParams {
  cluster: string;
  start_time: string;
}

// Create async thunk for fetching historical pod metrics
export const fetchPodMetricsHistory = createAsyncThunk(
  'podMetrics/fetchPodMetricsHistory',
  async (params: HistoricalMetricsParams, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${POD_METRICS_HISTORY_API_URL}?cluster=${params.cluster}&start_time=${params.start_time}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching historical pod metrics:', error);
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch historical pod metrics');
    }
  }
);

// Create the pod metrics slice
export const podMetricsSlice = createSlice({
  name: 'podMetrics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPodMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPodMetrics.fulfilled, (state, action: PayloadAction<PodMetricsResponse>) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.count = action.payload.count;
        state.cluster = action.payload.cluster;
      })
      .addCase(fetchPodMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle historical metrics
      .addCase(fetchPodMetricsHistory.pending, (state) => {
        state.loadingHistory = true;
        state.historyError = null;
      })
      .addCase(fetchPodMetricsHistory.fulfilled, (state, action: PayloadAction<PodMetricsResponse>) => {
        state.loadingHistory = false;
        state.historicalMetrics = action.payload.metrics;
      })
      .addCase(fetchPodMetricsHistory.rejected, (state, action) => {
        state.loadingHistory = false;
        state.historyError = action.payload as string;
      });
  },
});

export default podMetricsSlice.reducer; 